import { TaskContext, TaskDateInfo, TaskItem } from '../types/dashboardTypes';
import { formatDeadline } from '../../../lib/project-utils';
import { getInitialLoadingScreenState } from '../../../hooks/useInitialLoadingScreenManager';

// Helper object for loading screen state that doesn't use hooks directly
// This prevents "Invalid hook call" errors when used outside of components
export const loadingScreenManager = {
  isVisible: () => getInitialLoadingScreenState().isVisible,
  getLoadedRoutes: () => getInitialLoadingScreenState().loadedRoutes,
};

// Generate a color based on project ID (deterministic)
export const getProjectColor = (projectId: string): string => {
  const colors = [
    'bg-red-500',
    'bg-blue-500',
    'bg-green-500',
    'bg-yellow-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-indigo-500',
    'bg-teal-500',
    'bg-orange-500',
    'bg-cyan-500',
  ];

  // Simple hash function to convert project ID to a number
  const hash = projectId.split('').reduce((acc, char) => {
    return acc + char.charCodeAt(0);
  }, 0);

  return colors[hash % colors.length];
};

// Get project and topic names for a task
export const getTaskContext = (
  task: TaskItem,
  projects: Array<{ id: string; name: string }>
): TaskContext => {
  const topicInfo = task.topics as { project_id: string; name: string };
  if (!topicInfo)
    return { projectId: '', projectName: 'Unknown', topicName: 'Unknown', projectColor: '' };

  const projectId = topicInfo.project_id;
  const project = projects.find(p => p.id === projectId);

  return {
    projectId,
    projectName: project?.name || 'Unknown',
    topicName: topicInfo.name || 'Unknown',
    projectColor: getProjectColor(projectId),
  };
};

// Use the shared formatDeadline function from project-utils
export const formatDateInfo = (dateStr: string): TaskDateInfo | null => {
  const deadlineInfo = formatDeadline(dateStr);

  if (!deadlineInfo) return null;

  return {
    text: deadlineInfo.isOverdue ? deadlineInfo.lateByText : deadlineInfo.formatted,
    color: deadlineInfo.color,
    isPast: deadlineInfo.isOverdue,
    isToday: deadlineInfo.isToday,
  };
};

// Helper to get task categories

export const isOverdueTask = (task: TaskItem): boolean => {
  if (!task.deadline || task.status === 'done') return false;

  // Use our formatDeadline utility for consistent behavior
  const deadlineInfo = formatDeadline(task.deadline);
  if (!deadlineInfo) return false;

  // Double-check if today directly
  const dateObj = new Date(task.deadline);
  const today = new Date();
  const isSameDay =
    dateObj.getDate() === today.getDate() &&
    dateObj.getMonth() === today.getMonth() &&
    dateObj.getFullYear() === today.getFullYear();

  // Only include truly overdue tasks (past due, not today)
  return deadlineInfo.isOverdue && !deadlineInfo.isToday && !isSameDay && task.status !== 'done';
};

export const isTodayTask = (task: TaskItem): boolean => {
  if (!task.deadline || task.status === 'done') return false;

  // Use our formatDeadline utility for consistent behavior
  const deadlineInfo = formatDeadline(task.deadline);
  if (!deadlineInfo) return false;

  // Double-check the date to ensure it's truly today
  const dateObj = new Date(task.deadline);
  const today = new Date();
  const isSameDay =
    dateObj.getDate() === today.getDate() &&
    dateObj.getMonth() === today.getMonth() &&
    dateObj.getFullYear() === today.getFullYear();

  // Use both deadlineInfo.isToday AND direct date comparison for maximum accuracy
  return (deadlineInfo.isToday || isSameDay) && task.status !== 'done';
};

export const isThisWeekTask = (task: TaskItem): boolean => {
  if (!task.deadline || task.status === 'done') return false;

  // Use our formatDeadline utility for consistent behavior
  const deadlineInfo = formatDeadline(task.deadline);
  if (!deadlineInfo) return false;

  // Get today's date
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Calculate seven days from today
  const nextWeekEnd = new Date(today);
  nextWeekEnd.setDate(today.getDate() + 7);

  // Parse the deadline for date comparison
  const deadline = new Date(task.deadline);
  deadline.setHours(0, 0, 0, 0); // Normalize time for accurate comparison

  // Tasks for this view should be:
  // 1. Future tasks (not today)
  // 2. Within the next seven days (regardless of current day of week)
  // 3. Not done
  return (
    !deadlineInfo.isToday && // Not today
    deadline <= nextWeekEnd && // Within the next seven days
    deadline >= today && // Not in the past (today or future)
    task.status !== 'done' // Not done
  );
};

export const isHighlightedTask = (task: TaskItem): boolean => {
  // The dashboard task data is in the FormattedTask format which uses isHighlighted (camelCase)
  // This is consistent with the formatTask function used in React Query
  return task.isHighlighted && task.status !== 'done';
};

/**
 * Sort tasks by deadline, placing tasks with sooner deadlines at the top
 * Tasks without deadlines will be placed at the bottom
 */
export const sortTasksByDeadline = (tasks: TaskItem[]): TaskItem[] => {
  return [...tasks].sort((a, b) => {
    // If a task doesn't have a deadline, it goes to the bottom
    if (!a.deadline && !b.deadline) return 0;
    if (!a.deadline) return 1;
    if (!b.deadline) return -1;

    // Compare deadlines directly (ISO format strings can be compared)
    return a.deadline < b.deadline ? -1 : a.deadline > b.deadline ? 1 : 0;
  });
};
